import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sparkles, Loader2, Save, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { TokenChips, insertAtCursor, ReceiptCouponPreview } from './copyTokens'
import type { APIResponse } from '@/types'

/**
 * Comeback coupon — the auto-minted single-use promo code printed on
 * every receipt. Lives on the Promo Codes admin page (alongside
 * hand-created codes) since this is fundamentally a marketing feature,
 * not a receipt cosmetic.
 *
 * All five inputs are stored as one composite JSON blob under the
 * `receipt_thank_you_promo` app_settings key (legacy key, kept for
 * backwards compat — the UI surface was renamed in 2026-05).
 */

type ComebackCouponConfig = {
  enabled: boolean
  discount_type: 'percent' | 'flat'
  discount_value: number
  validity_days: number
  min_subtotal: number | null
  message: string
}

const SETTINGS_KEY = 'receipt_thank_you_promo'
// Kept in lock-step with marketing_promo_thank_you.go
// receiptThankYouDefaultMessage — the receipt renderer:
//   1. converts \n into <br> so the two-line layout prints as intended
//   2. always appends a "→" arrow at the end pointing at the code box
// So the saved message should NOT include the code or a trailing arrow.
// Newlines (\n\n for a blank line) are honoured on the receipt.
const DEFAULT_MESSAGE = 'Thanks for your visit!\n\nUse this code for {{discount}} off your next order'

const DEFAULTS: ComebackCouponConfig = {
  enabled: false,
  discount_type: 'percent',
  discount_value: 10,
  validity_days: 30,
  min_subtotal: null,
  message: DEFAULT_MESSAGE,
}

function parseConfig(raw: unknown): ComebackCouponConfig {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS }
  const r = raw as Record<string, unknown>
  const discountType = r.discount_type === 'flat' ? 'flat' : 'percent'
  return {
    enabled: r.enabled === true,
    discount_type: discountType,
    discount_value:
      typeof r.discount_value === 'number' && r.discount_value > 0
        ? r.discount_value
        : DEFAULTS.discount_value,
    validity_days:
      typeof r.validity_days === 'number' && r.validity_days > 0
        ? Math.round(r.validity_days)
        : DEFAULTS.validity_days,
    min_subtotal:
      typeof r.min_subtotal === 'number' && r.min_subtotal > 0 ? r.min_subtotal : null,
    message:
      typeof r.message === 'string' && r.message.trim() !== ''
        ? r.message
        : DEFAULTS.message,
  }
}

// Mirrors backend formatDiscountLabel in marketing_promo_thank_you.go:
// returns the BARE value ("10%" / "Rs. 500") so the message template
// can own the "off" wording naturally.
function formatDiscountLabel(type: 'percent' | 'flat', value: number): string {
  if (type === 'percent') {
    const trimmed = String(Number(value.toFixed(2)))
    return `${trimmed}%`
  }
  return `Rs. ${Math.round(value)}`
}

interface AdminComebackCouponSettingsProps {
  /** Optional click target for the "View past codes" link in the
   *  header. Toggles the admin's "Show auto-generated receipt codes"
   *  filter on the parent page so they can audit minted rows. */
  onViewPastCodes?: () => void
  pastCodesVisible?: boolean
}

export function AdminComebackCouponSettings({
  onViewPastCodes,
  pastCodesVisible,
}: AdminComebackCouponSettingsProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settingsQuery = useQuery<APIResponse<Record<string, unknown>>>({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 60_000,
  })

  const [draft, setDraft] = useState<ComebackCouponConfig>(DEFAULTS)
  const [expanded, setExpanded] = useState(false)
  const messageRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const raw = settingsQuery.data?.data?.[SETTINGS_KEY]
    const parsed = parseConfig(raw)
    setDraft(parsed)
    // Collapsed by default — the header shows a one-line summary of the
    // current config, so the card stays compact in the "Automatic coupons"
    // list until the owner chooses to edit it.
  }, [settingsQuery.data])

  const saveMutation = useMutation({
    mutationFn: async (next: ComebackCouponConfig) => {
      const clamped: ComebackCouponConfig = {
        enabled: next.enabled,
        discount_type: next.discount_type,
        discount_value: Math.max(
          0.01,
          next.discount_type === 'percent'
            ? Math.min(100, next.discount_value)
            : next.discount_value,
        ),
        validity_days: Math.max(1, Math.min(365, Math.round(next.validity_days))),
        min_subtotal:
          next.min_subtotal !== null && next.min_subtotal > 0 ? next.min_subtotal : null,
        message: next.message.trim() === '' ? DEFAULT_MESSAGE : next.message,
      }
      await apiClient.updateSetting(SETTINGS_KEY, clamped)
      return clamped
    },
    onSuccess: (_data, variables) => {
      toast({
        title: variables.enabled ? 'Comeback coupon enabled' : 'Comeback coupon saved',
        description: variables.enabled
          ? 'Every new receipt will print a unique code.'
          : undefined,
      })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Could not save', description: msg, variant: 'destructive' })
    },
  })

  const previewMessage = useMemo(() => {
    const discount = formatDiscountLabel(draft.discount_type, draft.discount_value)
    return draft.message
      .replace(/\{\{code\}\}/g, 'DV4BNF7K')
      .replace(/\{\{discount\}\}/g, discount)
  }, [draft.message, draft.discount_type, draft.discount_value])

  const previewExpiry = useMemo(() => {
    return new Date(Date.now() + draft.validity_days * 86400_000).toLocaleDateString(
      undefined,
      { day: 'numeric', month: 'short', year: 'numeric' },
    )
  }, [draft.validity_days])

  // One-line summary shown in the collapsed header so the row is
  // self-describing inside the "Automatic coupons" list.
  const summary = useMemo(() => {
    const label = formatDiscountLabel(draft.discount_type, draft.discount_value)
    const days = `${draft.validity_days} day${draft.validity_days === 1 ? '' : 's'}`
    const min = draft.min_subtotal ? ` · min order Rs. ${Math.round(draft.min_subtotal)}` : ''
    return `${label} off · valid ${days}${min}`
  }, [draft.discount_type, draft.discount_value, draft.validity_days, draft.min_subtotal])

  const loading = settingsQuery.isLoading

  // Dirty-state detection so the Save button feels responsive: only
  // pulses when there's something to save.
  const persisted = useMemo(
    () => parseConfig(settingsQuery.data?.data?.[SETTINGS_KEY]),
    [settingsQuery.data],
  )
  const dirty = useMemo(() => JSON.stringify(persisted) !== JSON.stringify(draft), [persisted, draft])

  return (
    <div
      id="comeback-coupon"
      className="rounded-xl border border-border/80 bg-gradient-to-br from-primary/[0.03] via-background to-background shadow-sm"
    >
      {/* Header row — always visible, single-glance: what is it + is it on.
          Bottom padding only when collapsed — when expanded the body
          provides its own and we don't want double-padding. */}
      <div
        className={cn(
          'flex cursor-pointer select-none items-start gap-3 rounded-t-xl px-5 pt-5 transition-colors hover:bg-foreground/[0.015]',
          !expanded && 'rounded-b-xl pb-5',
        )}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((x) => !x)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded((x) => !x)
          }
        }}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-4.5 w-4.5" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">Comeback coupon</h3>
            {draft.enabled ? (
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-sm font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                On
              </span>
            ) : (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Off
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {expanded
              ? 'Print a unique single-use coupon on every receipt to nudge customers back for a return visit.'
              : summary}
          </p>
        </div>
        <div
          className="flex shrink-0 items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            id="comeback-coupon-enabled"
            checked={draft.enabled}
            onCheckedChange={(v) => {
              setDraft((d) => ({ ...d, enabled: v }))
              if (v) setExpanded(true)
            }}
            disabled={loading}
            aria-label="Enable comeback coupon"
          />
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-5 px-5 pb-5 pt-5">
          <div className="h-px bg-border/60" />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.05fr_1fr]">
            {/* Left: inputs */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="comeback-discount-type" className="text-sm">
                    Discount type
                  </Label>
                  <Select
                    value={draft.discount_type}
                    onValueChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        discount_type: v === 'flat' ? 'flat' : 'percent',
                      }))
                    }
                  >
                    <SelectTrigger id="comeback-discount-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                      <SelectItem value="flat">Flat (Rs.)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="comeback-discount-value" className="text-sm">
                    {draft.discount_type === 'percent' ? 'Percent off' : 'Rupees off'}
                  </Label>
                  <Input
                    id="comeback-discount-value"
                    type="number"
                    inputMode="decimal"
                    min={1}
                    max={draft.discount_type === 'percent' ? 100 : undefined}
                    step={draft.discount_type === 'percent' ? 1 : 50}
                    value={draft.discount_value}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        discount_value: Number.isFinite(Number(e.target.value))
                          ? Number(e.target.value)
                          : d.discount_value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="comeback-validity" className="text-sm">
                    Valid for (days)
                  </Label>
                  <Input
                    id="comeback-validity"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={365}
                    value={draft.validity_days}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        validity_days: Number.isFinite(Number(e.target.value))
                          ? Number(e.target.value)
                          : d.validity_days,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="comeback-min" className="text-sm">
                    Minimum next-order (optional)
                  </Label>
                  <Input
                    id="comeback-min"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={50}
                    value={draft.min_subtotal ?? ''}
                    placeholder="No minimum"
                    onChange={(e) => {
                      const raw = e.target.value
                      if (raw === '') {
                        setDraft((d) => ({ ...d, min_subtotal: null }))
                        return
                      }
                      const n = Number(raw)
                      setDraft((d) => ({
                        ...d,
                        min_subtotal: Number.isFinite(n) && n > 0 ? n : null,
                      }))
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="comeback-message" className="text-sm">
                    Receipt copy
                  </Label>
                  {draft.message !== DEFAULT_MESSAGE && (
                    <button
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, message: DEFAULT_MESSAGE }))}
                      className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <Textarea
                  ref={messageRef}
                  id="comeback-message"
                  value={draft.message}
                  onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))}
                  rows={3}
                  placeholder={DEFAULT_MESSAGE}
                />
                <TokenChips
                  tokens={[{ label: 'Discount', token: '{{discount}}' }]}
                  onInsert={(token) =>
                    insertAtCursor(messageRef.current, token, draft.message, (v) =>
                      setDraft((d) => ({ ...d, message: v })),
                    )
                  }
                />
              </div>
            </div>

            {/* Right: live receipt preview */}
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                Receipt preview
              </Label>
              <ReceiptCouponPreview
                headline="SEE YOU SOON"
                message={previewMessage}
                code="DV4BNF7K"
                expiry={previewExpiry}
                minSubtotal={draft.min_subtotal}
              />
              <p className="text-sm text-muted-foreground">
                A new unique code prints on every paid receipt.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
            <div>
              {onViewPastCodes && (
                <button
                  type="button"
                  onClick={onViewPastCodes}
                  className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {pastCodesVisible ? 'Hide past comeback codes' : 'View past comeback codes ↓'}
                </button>
              )}
            </div>
            <Button
              onClick={() => saveMutation.mutate(draft)}
              disabled={saveMutation.isPending || loading || !dirty}
              size="sm"
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
